import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  CSSProperties,
} from 'react';
import styled from 'styled-components';
import { AvatarContext } from '../../contexts/AvatarContext';
import Lottie from 'lottie-react';

type AvatarContaierProps = {
  width: number;
  height: number;
};

const AvatarContainer = styled.div<AvatarContaierProps>`
  && {
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    position: relative;
  }
  && svg {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

// Types for animation objects
type AnimationData = object;
type SingleAnimation = { animation?: AnimationData } | null;

const getRandomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

const getNextAnimation = (queue: AnimationData[], animation: AnimationData) => {
  const withoutCurrentAnimation = queue.filter((q) => q !== animation);
  const { length } = withoutCurrentAnimation;
  return length
    ? withoutCurrentAnimation[getRandomInt(0, length - 1)]
    : queue[0];
};

const useAnimationQueue = (nextAnimationQueue: AnimationData[], singleAnimation: SingleAnimation) => {
  const [currentQueue, setCurrentQueue] = useState(nextAnimationQueue);
  const [currentAnimation, setCurrentAnimation] = useState(nextAnimationQueue[0]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update queue when it changes
  useEffect(() => {
    setCurrentQueue(nextAnimationQueue);
    if (nextAnimationQueue.length > 0) {
      setCurrentAnimation(nextAnimationQueue[0]);
    }
  }, [nextAnimationQueue]);

  // Handle single animation override
  useEffect(() => {
    if (singleAnimation?.animation) {
      setCurrentAnimation(singleAnimation.animation);
    }
  }, [singleAnimation]);

  // Animation complete handler - cycles to next animation
  const onAnimationComplete = useCallback(() => {
    if (currentQueue.length > 1 && !singleAnimation?.animation) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Set timeout for next animation
      timeoutRef.current = setTimeout(() => {
        const nextAnimation = getNextAnimation(currentQueue, currentAnimation);
        setCurrentAnimation(nextAnimation);
      }, 2000); // 2 second delay between animations
    }
  }, [currentQueue, currentAnimation, singleAnimation]);

  return { currentAnimation, onAnimationComplete };
};

type AvatarProps = {
  playLoop?: AnimationData;
  width?: number;
  height?: number;
  className?: string;
};

const Avatar = ({
  width = 40,
  height = 40,
  className,
  playLoop = undefined,
}: AvatarProps) => {
  const context = useContext(AvatarContext);
  const { animationQueue, singleAnimation } = context || {
    animationQueue: [],
    singleAnimation: null,
  };

  const { currentAnimation, onAnimationComplete } = useAnimationQueue(
    animationQueue,
    singleAnimation,
  );
  const animationData = playLoop || currentAnimation;

  if (!animationData) {
    return null;
  }

  const dimension = width || height || 140;
  const multiplier = 2.5;

  const style: CSSProperties = {
    position: 'absolute',
    width: `${dimension * multiplier}px`,
    height: `${dimension * multiplier}px`,
    top: `-${(dimension * multiplier) / 3.3}px`,
    left: `-${(dimension * multiplier) / 3.3}px`,
  };

  return (
    <AvatarContainer width={dimension} height={dimension} className={className}>
      <Lottie
        animationData={animationData}
        loop={playLoop !== undefined}
        style={style}
        onComplete={playLoop ? undefined : onAnimationComplete}
      />
    </AvatarContainer>
  );
};

export default Avatar;
